import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import { extractExtension, isWebEndpoint, displayCallerName, displayCallerNumber, displayQueue, computeQueuePrefix } from './utils';
import { getDispositionBadge } from './dispositionBadge';
import { analyticsHeaders } from '../../services/callcenterV2/analyticsHeaders';
import { legacyApiUrl } from '../../services/legacyApiBase';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { formatDuration, formatDateTime, formatTime, toSaoPauloDateString } from '../../utils/datetime';
import './config-styles.css';

const RECORDING_ROLES = ['root', 'admin'];

const RECORDING_ERRORS = {
  400: 'Identificador de chamada inválido.',
  403: 'Sem permissão para acessar esta gravação.',
  404: 'Gravação não encontrada para esta chamada.',
  413: 'Gravação acima de 100 MB: o servidor não a entrega nem para ouvir nem para baixar. Acione o suporte.',
  429: 'Muitas solicitações de gravação. Aguarde um instante.',
  503: 'Servidor de gravações ocupado. Tente novamente em instantes.',
};

const RECORDING_TIMEOUT_MESSAGE = 'Tempo esgotado ao recuperar a gravação. Tente novamente.';
const RECORDING_PLAYBACK_MESSAGE = 'Não foi possível reproduzir esta gravação no navegador. Use o download.';
const RECORDING_SLOW_NOTICE = 'Recuperando gravação do servidor de backup…';
const RECORDING_BUSY_NOTICE = 'Servidor de gravações ocupado. Tentando novamente…';

const RECORDING_FETCH_TIMEOUT_MS = 120000;
const RECORDING_SLOW_NOTICE_MS = 4000;
const RECORDING_BUSY_RETRIES = 2;
const RECORDING_BUSY_DELAY_MS = 15000;
const RECORDING_BUSY_MAX_DELAY_MS = 20000;
const RECORDING_REVOKE_DELAY_MS = 2000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const retryAfterDelayMs = (header) => {
  const seconds = Number.parseInt(header, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) return RECORDING_BUSY_DELAY_MS;
  return Math.min(seconds * 1000, RECORDING_BUSY_MAX_DELAY_MS);
};

const contentDispositionFilename = (header) => {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch (_e) {
      return encoded[1];
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];
  const bare = /filename=([^;]+)/i.exec(header);
  return bare ? bare[1].trim() : null;
};

const getApiBase = async () => {
  const envBase = process.env.REACT_APP_CALLCENTER_V2_BASE_URL;
  if (envBase) return envBase;
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};


function History() {
  const [loading, setLoading] = useState(false);
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [queues, setQueues] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [tenantPrefix, setTenantPrefix] = useState(null);
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const limit = 50;

  const today = toSaoPauloDateString(new Date());
  const weekAgo = toSaoPauloDateString(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

  const [filters, setFilters] = useState({
    startDate: weekAgo,
    endDate: today,
    queue: '',
    agent: '',
    disposition: '',
  });

  const fetchQueues = useCallback(async () => {
    try {
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      setTenantId(tid);
      const token = await getToken();
      const response = await fetch(`${apiBase}/v2/callcenter/queues`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tid,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.queues)
            ? data.queues
            : Array.isArray(data?.data)
              ? data.data
              : (data && typeof data === 'object')
                ? Object.values(data)
                : [];
        setQueues(list);
        setTenantPrefix(data?.namePrefix ?? null);
      }
    } catch (e) {}
  }, []);

  const fetchCalls = useCallback(async (pageNum = 0) => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tid = await getTenantId();
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(pageNum * limit),
      });

      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.queue) params.append('queue_key', filters.queue);
      if (filters.agent) params.append('agent_key', filters.agent);
      if (filters.disposition) params.append('disposition', filters.disposition);

      const response = await fetch(`${apiBase}/v2/analytics/history/${tid}/calls?${params}`, {
        headers: await analyticsHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setCalls(data.data || []);
        setTotal(data.total || 0);
        setPage(pageNum);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const currentUser = useCurrentUser();
  const canAccessRecordings = !!currentUser && RECORDING_ROLES.includes(currentUser.role);

  const [recordingCallId, setRecordingCallId] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [recordingNotice, setRecordingNotice] = useState(null);

  const recordingUrlRef = useRef(null);
  const slowNoticeTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
    if (recordingUrlRef.current) window.URL.revokeObjectURL(recordingUrlRef.current);
    if (slowNoticeTimerRef.current) clearTimeout(slowNoticeTimerRef.current);
  }, []);

  const replaceRecordingUrl = useCallback((url) => {
    if (recordingUrlRef.current) window.URL.revokeObjectURL(recordingUrlRef.current);
    if (!mountedRef.current) {
      if (url) window.URL.revokeObjectURL(url);
      recordingUrlRef.current = null;
      return;
    }
    recordingUrlRef.current = url;
    setRecordingUrl(url);
  }, []);

  const showRecordingError = useCallback((message) => {
    if (mountedRef.current) setRecordingError(message);
  }, []);

  const startRecordingRequest = useCallback((callId) => {
    setRecordingCallId(callId);
    setRecordingError(null);
    setRecordingNotice(null);
    setRecordingLoading(true);
    if (slowNoticeTimerRef.current) clearTimeout(slowNoticeTimerRef.current);
    slowNoticeTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setRecordingNotice(RECORDING_SLOW_NOTICE);
    }, RECORDING_SLOW_NOTICE_MS);
  }, []);

  const finishRecordingRequest = useCallback(() => {
    if (slowNoticeTimerRef.current) {
      clearTimeout(slowNoticeTimerRef.current);
      slowNoticeTimerRef.current = null;
    }
    if (!mountedRef.current) return;
    setRecordingNotice(null);
    setRecordingLoading(false);
  }, []);

  const resolveProtocol = useCallback(async (call) => {
    const uniqueId = call.unique_id || call.linked_id;
    if (!uniqueId) return null;
    const { ipRequest, token } = await accessLocalStorage.getUserLocalStorage();
    const response = await fetch(legacyApiUrl(ipRequest, 'api/download/protocol-by-uniqueid'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ uniqueid: uniqueId }),
    });
    if (!response.ok) {
      const err = new Error('protocol_lookup_failed');
      err.status = response.status;
      throw err;
    }
    const body = await response.json();
    return body?.data?.callprotocol || null;
  }, []);

  const fetchRecordingBlob = useCallback(async (protocol) => {
    const { ipRequest, token } = await accessLocalStorage.getUserLocalStorage();
    const url = legacyApiUrl(ipRequest, `api/download/file/${encodeURIComponent(protocol)}`);

    for (let attempt = 0; ; attempt += 1) {
      if (!mountedRef.current) {
        const abandoned = new Error('recording_download_abandoned');
        abandoned.code = 'ABANDONED';
        throw abandoned;
      }
      const controller = new AbortController();
      const abortTimer = setTimeout(() => controller.abort(), RECORDING_FETCH_TIMEOUT_MS);
      let response;
      try {
        response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
      } catch (transportError) {
        if (transportError?.name === 'AbortError') {
          const timedOut = new Error('recording_download_timeout');
          timedOut.code = 'TIMEOUT';
          throw timedOut;
        }
        throw transportError;
      } finally {
        clearTimeout(abortTimer);
      }

      if (response.ok) {
        return {
          blob: await response.blob(),
          filename: contentDispositionFilename(response.headers.get('Content-Disposition')),
        };
      }

      if (response.status === 503 && attempt < RECORDING_BUSY_RETRIES) {
        if (mountedRef.current) setRecordingNotice(RECORDING_BUSY_NOTICE);
        await wait(retryAfterDelayMs(response.headers.get('Retry-After')));
        continue;
      }

      const err = new Error('recording_download_failed');
      err.status = response.status;
      throw err;
    }
  }, []);

  const describeRecordingError = useCallback((err) => {
    if (err?.code === 'ABANDONED') return null;
    if (err?.code === 'TIMEOUT') return RECORDING_TIMEOUT_MESSAGE;
    return RECORDING_ERRORS[err?.status] || 'Falha ao obter a gravação desta chamada.';
  }, []);

  const handlePlayRecording = useCallback(async (call) => {
    const callId = call.unique_id || call.linked_id;
    if (recordingCallId === callId) {
      replaceRecordingUrl(null);
      setRecordingCallId(null);
      setRecordingError(null);
      setRecordingNotice(null);
      return;
    }

    startRecordingRequest(callId);
    replaceRecordingUrl(null);

    try {
      const protocol = await resolveProtocol(call);
      if (!protocol) {
        showRecordingError('Esta chamada não possui gravação.');
        return;
      }
      const { blob } = await fetchRecordingBlob(protocol);
      replaceRecordingUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      showRecordingError(describeRecordingError(err));
    } finally {
      finishRecordingRequest();
    }
  }, [recordingCallId, resolveProtocol, fetchRecordingBlob, replaceRecordingUrl, showRecordingError, describeRecordingError, startRecordingRequest, finishRecordingRequest]);

  const handleDownloadRecording = useCallback(async (call) => {
    const callId = call.unique_id || call.linked_id;
    startRecordingRequest(callId);
    replaceRecordingUrl(null);
    try {
      const protocol = await resolveProtocol(call);
      if (!protocol) {
        showRecordingError('Esta chamada não possui gravação.');
        return;
      }
      const { blob, filename } = await fetchRecordingBlob(protocol);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `${protocol}.wav`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), RECORDING_REVOKE_DELAY_MS);
      if (mountedRef.current) setRecordingCallId(null);
    } catch (err) {
      showRecordingError(describeRecordingError(err));
    } finally {
      finishRecordingRequest();
    }
  }, [resolveProtocol, fetchRecordingBlob, replaceRecordingUrl, showRecordingError, describeRecordingError, startRecordingRequest, finishRecordingRequest]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const apiBase = await getApiBase();
      const tid = await getTenantId();

      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.queue) params.append('queue_key', filters.queue);
      if (filters.agent) params.append('agent_key', filters.agent);
      if (filters.disposition) params.append('disposition', filters.disposition);

      const response = await fetch(`${apiBase}/v2/analytics/export/calls/${tid}?${params}`, {
        headers: await analyticsHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chamadas_${tid}_${toSaoPauloDateString(new Date())}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Erro ao exportar CSV:', e);
      alert('Erro ao exportar dados. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  useEffect(() => {
    fetchCalls(0);
  }, [fetchCalls]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCalls(0);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: weekAgo,
      endDate: today,
      queue: '',
      agent: '',
      disposition: '',
    });
  };

  const effectivePrefix = useMemo(() => {
    if (tenantPrefix) return tenantPrefix;
    const names = [
      ...queues.map((q) => q.name).filter(Boolean),
      ...calls.map((c) => c.queue_name).filter(Boolean),
    ];
    return computeQueuePrefix(tenantId, names) || null;
  }, [tenantPrefix, queues, calls, tenantId]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="ccv2-page-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>Historico de Chamadas</h1>
          <p>Consulte o historico detalhado de chamadas do Call Center</p>
        </div>
        <div className="ccv2-page-actions">
          <button 
            className="ccv2-btn ccv2-btn-secondary" 
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
        </div>
      </div>

      <form className="ccv2-filters" onSubmit={handleSearch}>
        <div className="ccv2-filter-group">
          <label>Data Inicio</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>
        <div className="ccv2-filter-group">
          <label>Data Fim</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
        <div className="ccv2-filter-group">
          <label>Fila</label>
          <select
            value={filters.queue}
            onChange={(e) => setFilters({ ...filters, queue: e.target.value })}
          >
            <option value="">Todas</option>
            {queues.filter((q) => q.ccv2MetricsEnabled !== false).map((q) => (
              <option key={q.queueKey || q.queue_key} value={q.queueKey || q.queue_key}>
                {displayQueue(q.name, effectivePrefix) || q.queueKey || q.queue_key}
              </option>
            ))}
          </select>
        </div>
        <div className="ccv2-filter-group">
          <label>Agente</label>
          <input
            type="text"
            placeholder="Interface do agente"
            value={filters.agent}
            onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
          />
        </div>
        <div className="ccv2-filter-group">
          <label>Status</label>
          <select
            value={filters.disposition}
            onChange={(e) => setFilters({ ...filters, disposition: e.target.value })}
          >
            <option value="">Todos</option>
            <option value="answered">Atendidas</option>
            <option value="abandoned">Abandonadas</option>
            <option value="short_abandon">Abandono curto</option>
            <option value="noanswer">Não Atendida</option>
          </select>
        </div>
        <div className="ccv2-filter-actions">
          <button type="submit" className="ccv2-btn ccv2-btn-primary">
            Buscar
          </button>
          <button type="button" className="ccv2-btn ccv2-btn-outline" onClick={handleClearFilters}>
            Limpar
          </button>
        </div>
      </form>

      <div className="ccv2-results-info">
        <span>{total} chamada(s) encontrada(s)</span>
        {totalPages > 1 && (
          <span className="ccv2-pagination-info">
            Pagina {page + 1} de {totalPages}
          </span>
        )}
      </div>

      <div className="ccv2-config-card">
        {loading ? (
          <div className="ccv2-config-loading">
            <div className="ccv2-spinner"></div>
          </div>
        ) : (
          <>
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Origem</th>
                    <th>Nome</th>
                    <th>Fila</th>
                    <th>Agente</th>
                    <th>Espera</th>
                    <th>Duração</th>
                    <th>Status</th>
                    {canAccessRecordings && <th>Gravação</th>}
                  </tr>
                </thead>
                <tbody>
                  {calls.length === 0 ? (
                    <tr>
                      <td colSpan={canAccessRecordings ? 9 : 8} className="ccv2-empty-row">
                        Nenhuma chamada encontrada para os filtros selecionados
                      </td>
                    </tr>
                  ) : (
                    calls.map((call, idx) => (
                      <tr key={call.unique_id || idx}>
                        <td className="ccv2-cell-datetime">
                          <div>{formatDateTime(call.entered_at)}</div>
                          {call.answered_at && (
                            <small className="ccv2-cell-secondary">
                              Atendida: {formatTime(call.answered_at, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </small>
                          )}
                        </td>
                        <td className="ccv2-cell-phone">{displayCallerNumber(call.caller_number) || '-'}</td>
                        <td>{displayCallerName(call.caller_name, call.caller_number) || '-'}</td>
                        <td title={call.queue_name || ''}>{displayQueue(call.queue_name, effectivePrefix)}</td>
                        <td>
                          <div>{call.agent_name || '-'}</div>
                          {(() => {
                            const ramal =
                              call.agentExtension ||
                              call.agent_extension ||
                              extractExtension(call.agent_interface);
                            if (!ramal && !call.agent_interface) return null;
                            const web = isWebEndpoint(call.agent_interface);
                            return (
                              <small
                                className="ccv2-cell-secondary"
                                title={call.agent_interface || ''}
                              >
                                {ramal || call.agent_interface}
                                {web ? ' (WebRTC)' : ''}
                              </small>
                            );
                          })()}
                        </td>
                        <td className="ccv2-cell-time">{formatDuration(call.wait_time)}</td>
                        <td className="ccv2-cell-time">{formatDuration(call.talk_time)}</td>
                        <td>{getDispositionBadge(call.disposition)}</td>
                        {canAccessRecordings && (
                          <td className="ccv2-cell-actions">
                            {(call.unique_id || call.linked_id) ? (
                              <>
                                <button
                                  type="button"
                                  className="ccv2-btn-icon"
                                  title="Ouvir gravação"
                                  disabled={recordingLoading}
                                  onClick={() => handlePlayRecording(call)}
                                >
                                  ▶
                                </button>
                                <button
                                  type="button"
                                  className="ccv2-btn-icon"
                                  title="Baixar gravação"
                                  disabled={recordingLoading}
                                  onClick={() => handleDownloadRecording(call)}
                                >
                                  ⬇
                                </button>
                              </>
                            ) : (
                              <span className="ccv2-cell-secondary" title="Chamada sem identificador de gravação">—</span>
                            )}
                            {recordingCallId === (call.unique_id || call.linked_id) && (
                              <div className="ccv2-recording-panel">
                                {recordingLoading && (
                                  <span className="ccv2-cell-secondary">{recordingNotice || 'Carregando…'}</span>
                                )}
                                {recordingError && <span className="ccv2-cell-danger">{recordingError}</span>}
                                {recordingUrl && (
                                  <audio
                                    controls
                                    autoPlay
                                    src={recordingUrl}
                                    style={{ width: '100%' }}
                                    onError={() => setRecordingError(RECORDING_PLAYBACK_MESSAGE)}
                                  >
                                    Seu navegador não suporta reprodução de áudio.
                                  </audio>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="ccv2-pagination">
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page === 0}
                  onClick={() => fetchCalls(0)}
                >
                  Primeira
                </button>
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page === 0}
                  onClick={() => fetchCalls(page - 1)}
                >
                  Anterior
                </button>
                <span className="ccv2-page-number">{page + 1} / {totalPages}</span>
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => fetchCalls(page + 1)}
                >
                  Proxima
                </button>
                <button
                  className="ccv2-btn ccv2-btn-sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => fetchCalls(totalPages - 1)}
                >
                  Ultima
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default History;
